import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import useStyles from "./styles";
import {
  SystemLogFilters,
  levelColor,
  useSystemLogStats,
  useSystemLogs,
} from "../../api/audit";

/**
 * Technical logs — what broke.
 *
 * The `system_logs` table has existed since July 2025 and had no read surface
 * at all: every investigation meant someone opening a SQL client against
 * production. This is that surface.
 *
 * Deliberately a different layout from the audit tab. An audit entry is a
 * short structured fact best shown as a table row; an error is a message plus
 * a stack trace, and the trace is the reason anyone opened the screen — so
 * rows expand in place rather than opening a drawer.
 */
export const SystemLogTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [filters, setFilters] = useState<SystemLogFilters>({
    page: 1,
    per_page: 25,
  });
  const [expanded, setExpanded] = useState<number | null>(null);

  const { logs, pagination, isLoading } = useSystemLogs(filters);
  const { stats } = useSystemLogStats();

  const setFilter = (key: keyof SystemLogFilters, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));

  const hasActiveFilters = Object.entries(filters).some(
    ([k, v]) => !["page", "per_page"].includes(k) && v
  );

  return (
    <>
      <div className={classes.filterBar}>
        <TextField
          size="small"
          label={t("audit.search", "Rechercher")}
          placeholder={t("audit.searchSystem", "Message, endpoint, fichier…")}
          value={filters.q ?? ""}
          onChange={(e) => setFilter("q", e.target.value)}
          sx={{ minWidth: 240 }}
        />

        <TextField
          select
          size="small"
          label={t("audit.level", "Niveau")}
          value={filters.level ?? ""}
          onChange={(e) => setFilter("level", e.target.value)}
        >
          <MenuItem value="">{t("audit.all", "Tous")}</MenuItem>
          {(stats?.levels ?? []).map((l) => (
            <MenuItem key={l} value={l}>
              {l}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("audit.type", "Type")}
          value={filters.type ?? ""}
          onChange={(e) => setFilter("type", e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t("audit.all", "Tous")}</MenuItem>
          {(stats?.types ?? []).map((ty) => (
            <MenuItem key={ty} value={ty}>
              {ty}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          type="date"
          label={t("audit.from", "Du")}
          InputLabelProps={{ shrink: true }}
          value={filters.date_from ?? ""}
          onChange={(e) => setFilter("date_from", e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label={t("audit.to", "Au")}
          InputLabelProps={{ shrink: true }}
          value={filters.date_to ?? ""}
          onChange={(e) => setFilter("date_to", e.target.value)}
        />

        {hasActiveFilters && (
          <Button
            size="small"
            color="inherit"
            startIcon={<RestartAltIcon />}
            onClick={() => setFilters({ page: 1, per_page: filters.per_page })}
          >
            {t("audit.reset", "Réinitialiser")}
          </Button>
        )}
      </div>

      <TableContainer component={Paper} className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>{t("audit.date", "Date")}</TableCell>
              <TableCell>{t("audit.level", "Niveau")}</TableCell>
              <TableCell>{t("audit.type", "Type")}</TableCell>
              <TableCell>{t("audit.message", "Message")}</TableCell>
              <TableCell>{t("audit.endpoint", "Endpoint")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box className={classes.emptyState}>
                    {t("audit.emptySystem", "Aucun journal technique.")}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() =>
                    setExpanded(expanded === log.id ? null : log.id)
                  }
                >
                  <TableCell>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                        transition: "transform .15s",
                        transform:
                          expanded === log.id ? "rotate(180deg)" : "none",
                      }}
                    />
                  </TableCell>
                  <TableCell
                    className={classes.monoSmall}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {log.created_at
                      ? new Date(log.created_at).toLocaleString("fr-FR")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.level}
                      color={levelColor(log.level)}
                    />
                  </TableCell>
                  <TableCell className={classes.monoSmall}>{log.type}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 420,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {log.message}
                  </TableCell>
                  <TableCell className={classes.monoSmall}>
                    {log.method} {log.endpoint}
                  </TableCell>
                </TableRow>

                {/*
                  Expanded detail. Rendered only when open — a stack trace can
                  be tens of kilobytes and mounting 25 of them per page would
                  make scrolling stutter for no benefit.
                */}
                {expanded === log.id && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ backgroundColor: "#fafafa" }}>
                      <Box sx={{ py: 2 }}>
                        {/*
                          The full message, first.

                          The table cell above truncates with an ellipsis, and
                          this panel used to jump straight to the metadata — so
                          the single most important field was unreachable at
                          any width. "Euromaster bookAppointment failed:
                          Internal error - A calendar error occ…" cut off
                          exactly where the diagnosis starts.
                        */}
                        <div className={classes.sectionTitle}>
                          {t("audit.message", "Message")}
                        </div>
                        <Box
                          className={classes.messageBlock}
                          sx={{ mb: 2 }}
                        >
                          {log.message}
                        </Box>

                        <div className={classes.metaGrid}>
                          <span className={classes.metaKey}>
                            {t("audit.user", "Utilisateur")}
                          </span>
                          <span className={classes.metaValue}>
                            {log.user_id ? `#${log.user_id}` : "—"}
                          </span>
                          <span className={classes.metaKey}>
                            {t("audit.ip", "IP")}
                          </span>
                          <span
                            className={`${classes.metaValue} ${classes.mono}`}
                          >
                            {log.ip_address ?? "—"}
                          </span>
                          {log.file_path && (
                            <>
                              <span className={classes.metaKey}>
                                {t("audit.file", "Fichier")}
                              </span>
                              <span
                                className={`${classes.metaValue} ${classes.monoSmall}`}
                              >
                                {log.file_path}
                                {log.line_number ? `:${log.line_number}` : ""}
                              </span>
                            </>
                          )}
                        </div>

                        {log.context && (
                          <>
                            <div className={classes.sectionTitle}>
                              {t("audit.context", "Contexte")}
                            </div>
                            <pre className={classes.jsonBlock}>
                              {JSON.stringify(log.context, null, 2)}
                            </pre>
                          </>
                        )}

                        {log.request_data && (
                          <>
                            <div className={classes.sectionTitle}>
                              {t("audit.requestData", "Données de la requête")}
                            </div>
                            <pre className={classes.jsonBlock}>
                              {JSON.stringify(log.request_data, null, 2)}
                            </pre>
                          </>
                        )}

                        {log.stack_trace && (
                          <Accordion
                            sx={{ mt: 2, boxShadow: "none", border: "1px solid #e0e0e0" }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <span className={classes.sectionTitle}>
                                {t("audit.stackTrace", "Stack trace")}
                              </span>
                            </AccordionSummary>
                            <AccordionDetails>
                              <pre className={classes.jsonBlock}>
                                {log.stack_trace}
                              </pre>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination?.total ?? 0}
        page={Math.max(0, (pagination?.current_page ?? 1) - 1)}
        onPageChange={(_e, page) =>
          setFilters((prev) => ({ ...prev, page: page + 1 }))
        }
        rowsPerPage={pagination?.per_page ?? 25}
        onRowsPerPageChange={(e) =>
          setFilters((prev) => ({
            ...prev,
            per_page: parseInt(e.target.value, 10),
            page: 1,
          }))
        }
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage={t("audit.rowsPerPage", "Lignes")}
      />
    </>
  );
};
